import { useState, useEffect, useMemo } from 'react';
import {
  onAuthStateChanged,
  signInAnonymously,
  signInWithPopup,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  User as FirebaseUser,
  signInWithCustomToken,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '@/lib/firebase';
import { runtimeEnv } from '@/lib/runtime-env';
import type { User } from '@/lib/types';
import { createDefaultUserProfile } from '@/lib/utils/profile';
import { signInWithUsernameAndPassword, linkProfile } from '@/lib/auth/client-auth-service';
import { mapAuthErrorToMessage } from '@/lib/auth/auth-errors';

export const MVP_FRICTIONLESS = false;
const PROFILE_LOAD_FAILURE =
  'Signed in, but Poker Knights could not load your profile. Restart the test server with pnpm.cmd run dev:test, then try again.';

export function useAuth() {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const createDefaultUser = (fbUser: FirebaseUser | { uid: string; email?: string | null; displayName?: string | null; photoURL?: string | null } | null): User => ({
    ...createDefaultUserProfile(fbUser ?? {
      uid: 'spectator',
      email: 'guest@pokerknights.local',
      displayName: 'Spectator',
      photoURL: null,
    }),
    roles: {
      player: true,
      app_admin: false,
      entertainment_admin: false,
    },
  });

  useEffect(() => {
    const allowFrictionlessFallback = runtimeEnv.isNonProduction && !runtimeEnv.isTest;
    // --- SECURE AUTH BYPASS START ---
    const params = new URLSearchParams(window.location.search);
    const legacyQueryRequestedTestMode = params.get('testMode') === 'true';
    
    if (runtimeEnv.isTest || (!runtimeEnv.isProduction && legacyQueryRequestedTestMode)) {
      sessionStorage.setItem('auth_test_mode', 'true');
    }

    const manualAuthSession = sessionStorage.getItem('auth_manual_login') === 'true';
    const manualLogoutSession = sessionStorage.getItem('auth_manual_logout') === 'true';
    const isAuthEntryRoute = window.location.pathname === '/auth';
    const shouldUseTestAuth =
      !manualAuthSession &&
      !manualLogoutSession &&
      !isAuthEntryRoute &&
      (runtimeEnv.isTest ||
        (!runtimeEnv.isProduction && sessionStorage.getItem('auth_test_mode') === 'true'));

    if (shouldUseTestAuth && !auth.currentUser) {
      let cancelled = false;

      const activateTestMode = async () => {
        try {
          const credential = await signInAnonymously(auth);
          if (cancelled) return;
          const testUser = createDefaultUser({
            uid: credential.user.uid,
            email: credential.user.email ?? 'test@example.com',
            displayName: credential.user.displayName ?? 'Test Knight',
            photoURL: credential.user.photoURL,
          });
          setFirebaseUser(credential.user);
          setUser(testUser);
        } catch (error) {
          console.warn('[AUTH-BYPASS] Test runtime anonymous sign-in failed:', error);
          if (cancelled) return;
          const mockFbUser = { uid: 'test-user-123', email: 'test@example.com', displayName: 'Test Knight' } as FirebaseUser;
          const testUser = createDefaultUser(mockFbUser);
          setFirebaseUser(mockFbUser);
          setUser(testUser);
        } finally {
          if (!cancelled) setLoading(false);
        }
      };

      activateTestMode();
      return () => {
        cancelled = true;
      };
    }
    // --- SECURE AUTH BYPASS END ---

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (fbUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', fbUser.uid));
           if (userDoc.exists()) {
            const userData = { id: userDoc.id, ...userDoc.data() } as User;
            setUser(userData);
            setAuthError(null);
           } else {
            console.log('No user doc found for UID:', fbUser.uid);
            try {
              const result = await linkProfile();
              if (result.customToken) {
                await signInWithCustomToken(auth, result.customToken);
                // onAuthStateChanged will fire again with the existing UID
                return;
              }
              if (result.linked) {
                const linkedDoc = await getDoc(doc(db, 'users', result.uid));
                if (linkedDoc.exists()) {
                  const linkedUserData = { id: linkedDoc.id, ...linkedDoc.data() } as User;
                  setUser(linkedUserData);
                  setAuthError(null);
                  setLoading(false);
                  return;
                }
              }
              // linkProfile returned linked:false or doc missing — treat as no profile
              setUser(null);
              setAuthError('No profile found for this email. Ask an admin to add you.');
            } catch (err) {
              const code = (err as { code?: string })?.code;
              if (code === 'NO_PROFILE_FOUND') {
                setAuthError('No profile found for this email. Ask an admin to add you.');
              } else {
                console.error('Link profile error:', err);
                setAuthError('Unable to complete sign-in. Please try again.');
              }
              setUser(null);
            }
          }
        } catch (err) {
          console.error('Error fetching user doc:', err);
          setUser(allowFrictionlessFallback ? createDefaultUser(fbUser) : null);
          setGoogleAccessToken(null);
          setAuthError(PROFILE_LOAD_FAILURE);
        }
      } else {
        setUser(allowFrictionlessFallback ? createDefaultUser(null) : null);
        setGoogleAccessToken(null);
        setAuthError(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const signIn = async () => {
    setAuthError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      sessionStorage.setItem('auth_manual_login', 'true');
      sessionStorage.removeItem('auth_manual_logout');
      const fbUser = result.user;
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const googleToken = credential?.accessToken || null;
      setGoogleAccessToken(googleToken);
      console.log('Signed in as:', fbUser.uid, fbUser.email, 'Google token:', !!googleToken);
    } catch (err) {
      console.error('Sign in error:', err);
      setAuthError(mapAuthErrorToMessage(err));
      throw err;
    }
  };

  const signInWithPassword = async (identifier: string, password: string) => {
    setAuthError(null);
    try {
      await signInWithUsernameAndPassword(identifier, password);
      sessionStorage.setItem('auth_manual_login', 'true');
      sessionStorage.removeItem('auth_manual_logout');
    } catch (err) {
      const message = mapAuthErrorToMessage(err);
      setAuthError(message);
      throw Object.assign(err instanceof Error ? err : new Error(message), { userMessage: message });
    }
  };

  const signOut = async () => {
    sessionStorage.removeItem('auth_manual_login');
    sessionStorage.setItem('auth_manual_logout', 'true');
    await firebaseSignOut(auth);
  };

  const effectiveUser = useMemo(() => {
    if (!user || !runtimeEnv.allowLocalAdminBypass) {
      return user;
    }
    return {
      ...user,
      roles: {
        player: user.roles?.player ?? false,
        entertainment_admin: user.roles?.entertainment_admin ?? false,
        app_admin: true,
      },
    };
  }, [user, runtimeEnv.allowLocalAdminBypass]);

  return {
    firebaseUser,
    user: effectiveUser,
    loading,
    googleAccessToken,
    authError,
    signIn,
    signInWithPassword,
    signOut,
  };
}
